import { useEffect, useMemo, useState } from 'react';

import { organizationsService } from '@/services/organizations.service';
import { usersService } from '@/services/users.service';
import type { Notification } from '@/types/notifications';

interface EnrichedNotification extends Notification {
  user?: {
    id: string;
    name: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
  };
}

/**
 * Hook to enrich notifications with user and organization data
 */
export const useEnrichedNotifications = (notifications: Notification[]) => {
  const [enrichedNotifications, setEnrichedNotifications] = useState<EnrichedNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create a stable string key from notification IDs to avoid infinite loops
  const notificationKey = useMemo(() => notifications.map(n => n.id).join(','), [notifications]);

  useEffect(() => {
    const enrichNotifications = async () => {
      if (!notifications.length) {
        setEnrichedNotifications([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get unique user and organization IDs
        const userIds = [...new Set(notifications.map(n => n.userId))];
        const organizationIds = [...new Set(notifications.map(n => n.organizationId))];

        // Fetch all users and organizations in parallel
        const [usersResults, organizationsResults] = await Promise.allSettled([
          Promise.all(userIds.map(id => usersService.getUser(id).catch(() => null))),
          Promise.all(
            organizationIds.map(id => organizationsService.getOrganization(id).catch(() => null))
          ),
        ]);

        // Create lookup maps
        const usersMap = new Map();
        const organizationsMap = new Map();

        if (usersResults.status === 'fulfilled') {
          usersResults.value.forEach((user, index) => {
            if (user) {
              usersMap.set(userIds[index], {
                id: user.id,
                name: user.name,
                email: user.email,
              });
            }
          });
        }

        if (organizationsResults.status === 'fulfilled') {
          organizationsResults.value.forEach((org, index) => {
            if (org) {
              organizationsMap.set(organizationIds[index], {
                id: org.id,
                name: org.name,
              });
            }
          });
        }

        // Enrich notifications
        const enriched = notifications.map(notification => ({
          ...notification,
          user: usersMap.get(notification.userId),
          organization: organizationsMap.get(notification.organizationId),
        }));

        setEnrichedNotifications(enriched);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Error al cargar las notificaciones'));
      } finally {
        setIsLoading(false);
      }
    };

    void enrichNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationKey]);

  return { enrichedNotifications, isLoading, error };
};
